import { ThemeIcon } from '@src/theme/ThemeIcon';
import type { ReactNode } from 'react';
import type { CardInfo } from '@lub0v/santa-utils';
import {
  Badge,
  Button,
  Cluster,
  LinkIcon,
  EyeIcon,
  MailIcon,
  MessageIcon,
  Menu,
  ParticipantFace,
  Row,
  Stack,
  Typography,
  type BadgeTone,
  type MenuItem,
} from '@src/components';
import './ParticipantRow.css';

export interface ParticipantRowProps {
  card: CardInfo;
  selection?: ReactNode;
  /** Position in the list, used to pick a face when the card has no picture. */
  index: number;
  /** What this row is called on the page: 1 for the first participant. */
  ordinal: number;
  /** Omitted for participants who have not been invited yet. */
  status?: string;
  statusTone?: BadgeTone;
  statusDisplay?: 'badge' | 'text';
  nameIndicators?: ReactNode;
  statusColumns?: ReactNode;
  tableRow?: boolean;
  /** Shown beside the name of the organizer's own card. */
  youLabel?: string;
  /** Makes the name itself a way in to renaming, as in v1. */
  onRename?: () => void;
  onPreview?: () => void;
  previewLabel?: string;
  previewMenuLabel?: string;
  /** Omitted when a personal link cannot be issued — the card is already claimed. */
  onCopy?: () => void;
  copyLabel: string;
  onEmail?: () => void;
  emailLabel: string;
  description?: ReactNode;
  onMessage?: () => void;
  messageLabel?: string;
  /** Copy, email, and rename stay on screen but do not run. */
  locked?: boolean;
  /** Hide the row number — the quick-draw dashboard list does not use it. */
  showOrdinal?: boolean;
  menu: MenuItem[];
  menuLabel: string;
  testId?: string;
}

/**
 * One participant as the organizer sees them: who they are, how far they have
 * got, and the two invitations that move them along. Everything rarer lives in
 * the row menu.
 */
export function ParticipantRow({
  card,
  selection,
  index,
  ordinal,
  status,
  statusTone,
  statusDisplay = 'badge',
  nameIndicators,
  statusColumns,
  tableRow = false,
  youLabel,
  onRename,
  onPreview,
  previewLabel,
  previewMenuLabel,
  onCopy,
  copyLabel,
  onEmail,
  emailLabel,
  description,
  onMessage,
  messageLabel,
  locked = false,
  showOrdinal = true,
  menu,
  menuLabel,
  testId,
}: ParticipantRowProps) {
  // Inline shortcuts and the overflow menu share the same actions at every width.
  const shortcuts: MenuItem[] = [];
  if (onCopy)
    shortcuts.push({
      id: 'copy',
      label: copyLabel,
      icon: <ThemeIcon name="link" fallback={<LinkIcon />} />,
      disabled: locked,
      onSelect: onCopy,
    });
  if (onEmail)
    shortcuts.push({
      id: 'email',
      label: emailLabel,
      icon: <ThemeIcon name="mail" fallback={<MailIcon />} />,
      disabled: locked,
      onSelect: onEmail,
    });
  if (onMessage && messageLabel)
    shortcuts.push({
      id: 'message',
      label: messageLabel,
      icon: <ThemeIcon name="message" fallback={<MessageIcon />} />,
      onSelect: onMessage,
    });
  const menuItems = [
    ...(onPreview && previewMenuLabel ? [{ id: 'preview', label: previewMenuLabel, icon: <EyeIcon />, onSelect: onPreview }] : []),
    ...shortcuts.filter((action) => !menu.some((item) => item.id === action.id)),
    ...menu,
  ];

  const identity = (
        <Cluster align="center" gap={3} wrap={false} className="participant-row__identity">
          {showOrdinal && !tableRow ? (
            <Typography as="span" role="meta/helper" className="participant-row__ordinal">
              {String(ordinal)}
            </Typography>
          ) : null}
          {!tableRow && selection}
          {/* Dimmed until the card has been opened, so a glance down the list
              shows who is still only invited. */}
          <ParticipantFace curiositySource="participants_table" card={card} index={index} muted={!card.isVerified} />
          <Stack gap={2} className={`participant-row__copy${!card.isVerified ? ' participant-row__copy--unjoined' : ''}`}>
            <Cluster gap={2} align="center">
              {onPreview && previewLabel ? (
                <Typography as="button" role="title/sub" className="participant-row__name participant-row__name-preview" type="button" onClick={onPreview} aria-label={previewLabel} testId={testId ? `${testId}-preview` : undefined}>
                  {card.username}<span className="participant-row__name-eye" aria-hidden="true">&nbsp;<EyeIcon /></span>
                </Typography>
              ) : onRename && !locked ? (
                <Typography as="button" role="title/sub" className="participant-row__name" type="button" onClick={onRename} testId={testId ? `${testId}-name` : undefined}>{card.username}</Typography>
              ) : <Typography as="p" role="title/sub" className="participant-row__name">{card.username}</Typography>}
              {youLabel ? <Typography as="span" role="meta/helper">{youLabel}</Typography> : null}
              {nameIndicators}
            </Cluster>
            {description}
            {status ? (
              <Cluster className="participant-row__status">
                {statusDisplay === 'text' ? (
                  <span
                    className={`participant-row__status-text participant-row__status-text--${statusTone || 'neutral'}`}
                    data-testid={testId ? `${testId}-status` : undefined}
                  >
                    {status}
                  </span>
                ) : (
                  <Badge tone={statusTone} testId={testId ? `${testId}-status` : undefined}>
                    {status}
                  </Badge>
                )}
              </Cluster>
            ) : null}
          </Stack>
        </Cluster>
  );
  const actions = (
        <Cluster gap={2} align="center" wrap={false} className="participant-row__actions">
          <Cluster gap={2} align="center" wrap={false} className="participant-row__inline-actions">
            {onCopy ? (
              <Button
                variant="ghost"
                size="medium"
                icon={<LinkIcon />}
                aria-label={copyLabel}
                title={copyLabel}
                disabled={locked}
                onClick={onCopy}
                testId={testId ? `${testId}-copy` : undefined}
              />
            ) : null}
            {onEmail ? (
              <Button
                variant="ghost"
                size="medium"
                icon={<MailIcon />}
                aria-label={emailLabel}
                title={emailLabel}
                disabled={locked}
                onClick={onEmail}
                testId={testId ? `${testId}-email` : undefined}
              />
            ) : null}
            {onMessage ? (
              <Button
                variant="ghost"
                size="medium"
                icon={<MessageIcon />}
                aria-label={messageLabel}
                title={messageLabel}
                onClick={onMessage}
                testId={testId ? `${testId}-message` : undefined}
              />
            ) : null}
          </Cluster>
          {menuItems.length ? (
            <Menu
              items={menuItems}
              label={menuLabel}
              size="medium"
              testId={testId ? `${testId}-menu` : undefined}
            />
          ) : null}
        </Cluster>
  );
  if (tableRow) return (
    <tr className="participant-data-row" data-testid={testId}>
      <td><div className="participant-data-row__person"><span className="participant-data-row__number-select"><span>{ordinal}</span>{selection}</span>{identity}</div></td>
      {statusColumns}
      <td>{actions}</td>
    </tr>
  );
  return (
    <Row size="large" align="center" className={statusColumns ? 'participant-row--columns' : undefined} testId={testId}>
      <Cluster justify="between" align="center" gap={4} wide wrap={false}>
        {identity}{statusColumns}{actions}
      </Cluster>
    </Row>
  );
}
